import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, AlertTriangle, ArrowUpDown, Building2, Download, ExternalLink, IndianRupee, Landmark, RefreshCw, Search, Wallet, X } from "lucide-react";
import { EmptyState, KpiCard, StatusBadge } from "./common";
import { DrilldownBanner, useUrlFilters } from "./DrilldownBanner";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "./FetchBar";
import { PaginationBar, TableRowsSkeleton, usePagedList } from "./PaginationBar";
import { TerritoryFilterSelects, useTerritoryMasters, namesOf } from "./TerritoryFilter";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { downloadBlob, errMsg, fmtDate, fmtINR } from "../lib/api";
import { useLiveCollections } from "../lib/liveCollections";

// URL keys are prefixed so they never collide with the other Collections tabs on the same page
export const AC_FILTER_KEYS = ["ac_search", "ac_zone_id", "ac_ward_id", "ac_mohalla_id", "ac_sort"];
const API_KEY = { ac_search: "search", ac_zone_id: "zone_id", ac_ward_id: "ward_id", ac_mohalla_id: "mohalla_id", ac_sort: "sort" };
const SORTS = [["advance_desc", "Largest advance first"], ["demand_desc", "Highest demand first"], ["paid_desc", "Most paid first"],
  ["last_payment_desc", "Latest payment first"], ["pin", "PIN (A → Z)"]];
const SOURCE_LABEL = { excel_upload: "Excel Upload", manual_entry: "Manual Entry", new_survey: "Survey", reassessment: "Reassessment", third_party: "Third Party" };

/**
 * Phase 150 — Collections → Advance Credit: every property whose payments exceeded its demand and that still holds
 * advance credit (PIN, owner, zone / ward / mohalla, total demand, total paid, advance balance). Zone / Ward / Mohalla
 * + search filters, Excel export of the current filter. Loads on Fetch (Phase 133 pattern); live via the ledger version.
 */
export function AdvanceCreditReport({ code, readOnly = false }) {
  const { filters, patch, clear, arrivedFiltered } = useUrlFilters(AC_FILTER_KEYS);
  const masters = useTerritoryMasters(code);
  const gateFilters = useMemo(() => { const { ac_sort, ...rest } = filters; return rest; }, [filters]);
  const gate = useFetchGate(gateFilters, { autoFetch: arrivedFiltered });
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = {};
    const src = gate.applied || {};
    AC_FILTER_KEYS.forEach((k) => { const v = k === "ac_sort" ? filters.ac_sort : src[k]; if (v) p[API_KEY[k]] = v; });
    return p;
  }, [gate.appliedKey, filters.ac_sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: payments anywhere refresh the balances here
  const list = usePagedList(`/t/${code}/collections/advance-credit`, { params, initialLimit: 25, enabled: gate.fetched, reloadKey: `${gate.version}:${live}` });
  const summary = list.data?.summary;
  const applied = gate.applied || {};
  const active = AC_FILTER_KEYS.filter((k) => k !== "ac_sort" && applied[k]);
  const clearAll = () => { clear(); gate.reset(); };

  // Phase 176: each level may hold several ids -> "Ward 1, Ward 5"
  const zoneName = namesOf(applied.ac_zone_id, masters.zones, "zone_name", "Zone");
  const wardName = namesOf(applied.ac_ward_id, masters.wards, "ward_name", "Ward");
  const mohallaName = namesOf(applied.ac_mohalla_id, masters.mohallas, "mohalla_name", "Mohalla");

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      await downloadBlob(`/t/${code}/collections/advance-credit/export${qs ? `?${qs}` : ""}`,
        `${code}_advance_credit_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Advance credit report exported", { description: `${(list.total || 0).toLocaleString("en-IN")} propert${list.total === 1 ? "y" : "ies"} · ${fmtINR(summary?.advance_credit || 0)} advance` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const chips = gate.fetched ? [
    { key: "ac_zone_id", label: "Zone", value: applied.ac_zone_id ? (zoneName || "…") : "" },
    { key: "ac_ward_id", label: "Ward", value: applied.ac_ward_id ? (wardName || "…") : "" },
    { key: "ac_mohalla_id", label: "Mohalla", value: applied.ac_mohalla_id ? (mohallaName || "…") : "" },
    { key: "ac_search", label: "Search", value: applied.ac_search },
  ] : [];

  return (
    <div data-testid="collections-advance-credit">
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => list.reload()} disabled={!gate.fetched || list.loading} data-testid="ac-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {!readOnly && <Button size="sm" onClick={exportExcel} disabled={exporting || !gate.fetched || list.total === 0} data-testid="ac-export-button">
          <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Exporting…" : "Export Excel"}
        </Button>}
      </div>

      <DrilldownBanner chips={chips} onClear={clearAll} testId="ac-drilldown" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="ac-summary">
        <KpiCard label="Properties with advance" value={gate.fetched ? (summary?.properties ?? 0) : "—"} icon={Building2} testId="ac-kpi-properties"
          sub={!gate.fetched ? "click Fetch to load" : summary ? `${(summary.fully_paid || 0).toLocaleString("en-IN")} with nothing due` : undefined} />
        <KpiCard label="Total demand" value={gate.fetched ? (summary?.total_demand ?? 0) : "—"} money={gate.fetched} icon={Landmark} testId="ac-kpi-demand" />
        <KpiCard label="Total paid" value={gate.fetched ? (summary?.total_collected ?? 0) : "—"} money={gate.fetched} icon={IndianRupee} testId="ac-kpi-paid" />
        <KpiCard label="Advance balance" value={gate.fetched ? (summary?.advance_credit ?? 0) : "—"} money={gate.fetched} icon={Wallet} testId="ac-kpi-advance"
          sub={gate.fetched && summary?.balance_due > 0 ? `${fmtINR(summary.balance_due)} still due on newer demands` : undefined} />
      </div>

      {list.data?.warning && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-3 py-2 text-sm" role="status" data-testid="ac-capped-warning">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" /> {list.data.warning}
        </div>
      )}

      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="ac-filter-form">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)] gap-3 items-end">
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Search</p>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9 h-10" placeholder="Search PIN, owner, address, mobile…" value={filters.ac_search}
                  onChange={(e) => patch({ ac_search: e.target.value })} data-testid="ac-search-input" />
              </div>
            </div>
            <TerritoryFilterSelects masters={masters} testIdPrefix="ac"
              value={{ zoneId: filters.ac_zone_id, wardId: filters.ac_ward_id, mohallaId: filters.ac_mohalla_id }}
              onChange={(v) => patch({ ac_zone_id: v.zoneId || "", ac_ward_id: v.wardId || "", ac_mohalla_id: v.mohallaId || "" })} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.ac_sort || "advance_desc"} onValueChange={(v) => patch({ ac_sort: v === "advance_desc" ? "" : v })}>
              <SelectTrigger className="w-52 h-10" data-testid="ac-sort-select">
                <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" /><SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`ac-sort-option-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-auto">
              <FetchDirtyHint dirty={gate.dirty} testId="ac-fetch-dirty" />
              <FetchButton type="submit" size="sm" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched} testId="ac-fetch-button" />
              {AC_FILTER_KEYS.some((k) => k !== "ac_sort" && filters[k]) && (
                <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clearAll} data-testid="ac-clear-filters">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
        </FetchForm>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId="ac-fetch-prompt" title="Advance payments report"
              subtitle="Lists every property whose payments exceeded its demand and still holds advance credit. Pick a zone, ward or mohalla (or leave all), then click Fetch." />
          ) : list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="ac-error">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Could not load the advance credit report.</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid="ac-error-retry">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          ) : !list.data ? (
            <TableRowsSkeleton rows={8} cols={7} testId="ac-skeleton" />
          ) : list.items.length === 0 ? (
            <EmptyState title="No properties hold advance credit" subtitle={active.length ? "Try clearing a filter or widening the area, then Fetch again." : "Advance credit appears when a payment is larger than the demand it was applied to."}
              action={active.length ? <Button variant="outline" size="sm" onClick={clearAll} data-testid="ac-empty-clear">Clear filters</Button> : null} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="ac-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead className="hidden md:table-cell">Zone / Ward / Mohalla</TableHead>
                      <TableHead className="text-right">Total demand</TableHead>
                      <TableHead className="text-right">Total paid</TableHead>
                      <TableHead className="text-right">Advance balance</TableHead>
                      <TableHead className="hidden sm:table-cell">Status</TableHead>
                      <TableHead className="w-10" aria-label="Open" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((r) => {
                      const key = r.pin || r.id;
                      return (
                        <TableRow key={r.id} className="hover:bg-muted/60" data-testid={`ac-row-${key}`}>
                          <TableCell>
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-mono text-sm text-primary" data-testid={`ac-row-pin-${key}`}>{r.pin || "—"}</span>
                              {r.source === "third_party" && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200">Third Party</Badge>}
                            </span>
                            <p className="text-xs text-muted-foreground truncate max-w-[240px]" data-testid={`ac-row-owner-${key}`}>{r.owner_name || "—"}</p>
                            <p className="text-[11px] text-muted-foreground md:hidden">{[r.zone_label, r.ward_label, r.mohalla_label].filter(Boolean).join(" • ") || "—"}</p>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-sm">
                            <p>{r.zone_label || "—"}{r.ward_label ? ` • ${r.ward_label}` : ""}</p>
                            <p className="text-xs text-muted-foreground">
                              {[r.mohalla_label, r.source && r.source !== "third_party" ? (SOURCE_LABEL[r.source] || r.source) : null].filter(Boolean).join(" • ") || "—"}
                            </p>
                          </TableCell>
                          <TableCell className="text-right tabular-nums whitespace-nowrap" data-testid={`ac-row-demand-${key}`}>{fmtINR(r.total_demand)}</TableCell>
                          <TableCell className="text-right tabular-nums whitespace-nowrap text-[hsl(var(--success))]" data-testid={`ac-row-paid-${key}`}>
                            {fmtINR(r.total_collected)}
                            <p className="text-[10px] text-muted-foreground font-normal">{r.payments_count} receipt{r.payments_count === 1 ? "" : "s"}{r.last_payment_date ? ` · last ${fmtDate(r.last_payment_date)}` : ""}</p>
                          </TableCell>
                          <TableCell className="text-right tabular-nums whitespace-nowrap font-semibold text-amber-800" data-testid={`ac-row-advance-${key}`}>
                            {fmtINR(r.advance_credit)}
                            {r.balance_due > 0.004 && <p className="text-[10px] text-destructive font-normal">{fmtINR(r.balance_due)} due</p>}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {r.has_demand ? <StatusBadge status={r.payment_status} />
                              : <Badge variant="outline" className="bg-muted text-muted-foreground font-normal" data-testid="status-badge-no-demand">No demand</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Open property record" data-testid={`ac-open-property-${key}`}>
                              <Link to={`/${code}/admin/properties?focus=${r.id}`} aria-label={`Open property ${r.pin || ""}`}><ExternalLink className="h-3.5 w-3.5" /></Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId="ac-pagination" />
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <p className="mt-3 text-[11px] text-muted-foreground" data-testid="ac-note">
        Advance balance is money received over and above the demands of the property (for example a legacy payment larger than the
        outstanding amount). It stays with the property until a new demand is raised and settled against it.
      </p>
    </div>
  );
}

export default AdvanceCreditReport;
